export interface PlacePrediction {
  placeId: string
  mainText: string
  secondaryText: string
  description: string
}

export interface PlaceCoordinates {
  latitude: number
  longitude: number
  formattedAddress: string
}

export interface SelectedLocation {
  label: string
  latitude?: number
  longitude?: number
  placeId?: string
}

/** Default search radius when sorting rooms by distance from a place */
export const DEFAULT_SEARCH_RADIUS_KM = 15

type GoogleMapsWindow = Window & {
  google?: {
    maps: {
      Geocoder: new () => {
        geocode: (
          request: { address?: string; placeId?: string; componentRestrictions?: { country: string } },
          callback: (
            results: Array<{
              formatted_address: string
              geometry: { location: { lat: () => number; lng: () => number } }
            }> | null,
            status: string,
          ) => void,
        ) => void
      }
      GeocoderStatus: { OK: string }
      places: {
        AutocompleteService: new () => {
          getPlacePredictions: (
            request: {
              input: string
              componentRestrictions?: { country: string | string[] }
              types?: string[]
            },
            callback: (
              predictions: Array<{
                place_id: string
                description: string
                structured_formatting: {
                  main_text: string
                  secondary_text?: string
                }
              }> | null,
              status: string,
            ) => void,
          ) => void
        }
        PlacesService: new (el: HTMLElement) => {
          getDetails: (
            request: { placeId: string; fields: string[] },
            callback: (
              place: {
                formatted_address?: string
                geometry?: { location?: { lat: () => number; lng: () => number } }
              } | null,
              status: string,
            ) => void,
          ) => void
        }
        PlacesServiceStatus: { OK: string }
      }
    }
  }
  __aonlaGoogleMapsInit?: () => void
}

let loadPromise: Promise<void> | null = null

export function getGoogleMapsApiKey(): string {
  return (import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '').trim()
}

export function isGoogleMapsConfigured(): boolean {
  return Boolean(getGoogleMapsApiKey())
}

export function loadGoogleMaps(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google Maps is only available in the browser'))
  }

  const win = window as GoogleMapsWindow
  if (win.google?.maps?.places) {
    return Promise.resolve()
  }

  if (loadPromise) {
    return loadPromise
  }

  const apiKey = getGoogleMapsApiKey()
  if (!apiKey) {
    return Promise.reject(new Error('VITE_GOOGLE_MAPS_API_KEY is not configured'))
  }

  loadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-aonla-google-maps]')
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true })
      existing.addEventListener('error', () => reject(new Error('Failed to load Google Maps')), { once: true })
      return
    }

    win.__aonlaGoogleMapsInit = () => {
      delete win.__aonlaGoogleMapsInit
      resolve()
    }

    const script = document.createElement('script')
    script.dataset.aonlaGoogleMaps = 'true'
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&callback=__aonlaGoogleMapsInit`
    script.async = true
    script.defer = true
    script.onerror = () => {
      loadPromise = null
      reject(new Error('Failed to load Google Maps'))
    }
    document.head.appendChild(script)
  })

  return loadPromise
}

export async function fetchPlacePredictions(input: string): Promise<PlacePrediction[]> {
  const trimmed = input.trim()
  if (trimmed.length < 2) {
    return []
  }

  await loadGoogleMaps()
  const win = window as GoogleMapsWindow
  const googleMaps = win.google?.maps
  if (!googleMaps?.places) {
    return []
  }

  const service = new googleMaps.places.AutocompleteService()
  const OK = googleMaps.places.PlacesServiceStatus.OK

  return new Promise((resolve) => {
    service.getPlacePredictions(
      {
        input: trimmed,
        componentRestrictions: { country: 'in' },
        types: ['geocode'],
      },
      (predictions, status) => {
        if (status !== OK || !predictions?.length) {
          resolve([])
          return
        }

        resolve(
          predictions.map((p) => ({
            placeId: p.place_id,
            mainText: p.structured_formatting.main_text,
            secondaryText: p.structured_formatting.secondary_text || '',
            description: p.description,
          })),
        )
      },
    )
  })
}

export async function resolveSearchCoordinates(options: {
  placeId?: string
  address?: string
}): Promise<PlaceCoordinates | null> {
  const address = options.address?.trim()
  if (!options.placeId && !address) {
    return null
  }

  if (!isGoogleMapsConfigured()) {
    return null
  }

  await loadGoogleMaps()
  const win = window as GoogleMapsWindow
  const googleMaps = win.google?.maps
  if (!googleMaps) {
    return null
  }

  const OK_GEO = googleMaps.GeocoderStatus.OK
  const OK_PLACES = googleMaps.places.PlacesServiceStatus.OK

  if (options.placeId) {
    const stub = document.createElement('div')
    const placesService = new googleMaps.places.PlacesService(stub)
    const fromPlaceId = await new Promise<PlaceCoordinates | null>((resolve) => {
      placesService.getDetails(
        { placeId: options.placeId!, fields: ['geometry', 'formatted_address'] },
        (place, status) => {
          const loc = place?.geometry?.location
          if (status !== OK_PLACES || !loc) {
            resolve(null)
            return
          }
          resolve({
            latitude: loc.lat(),
            longitude: loc.lng(),
            formattedAddress: place?.formatted_address || address || '',
          })
        },
      )
    })
    if (fromPlaceId) {
      return fromPlaceId
    }
  }

  if (!address) {
    return null
  }

  const geocoder = new googleMaps.Geocoder()
  return new Promise((resolve) => {
    geocoder.geocode(
      { address, componentRestrictions: { country: 'in' } },
      (results, status) => {
        const first = results?.[0]
        const loc = first?.geometry?.location
        if (status !== OK_GEO || !loc) {
          resolve(null)
          return
        }
        resolve({
          latitude: loc.lat(),
          longitude: loc.lng(),
          formattedAddress: first.formatted_address || address,
        })
      },
    )
  })
}
